import React, { useEffect, useState, useMemo } from 'react';
import { fetchShareAnalytics, type ShareAnalyticsSummary } from '../services/supabase';
import {
  BarChart3,
  Eye,
  Users,
  Play,
  CheckCircle2,
  Share2,
  Clock,
  Loader2,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

interface ShareAnalyticsProps {
  projectId: string;
  shareToken: string | null;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return '< 1s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remainder = s % 60;
  return `${m}m ${remainder}s`;
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatTimezone(tz: string): string {
  const parts = tz.split('/');
  return parts[parts.length - 1].replace(/_/g, ' ');
}

const DEVICE_ICONS: Record<string, typeof Monitor> = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
};

const PLATFORM_COLORS: Record<string, string> = {
  copy: 'bg-slate-500',
  whatsapp: 'bg-green-500',
  facebook: 'bg-blue-600',
  twitter: 'bg-sky-500',
  email: 'bg-amber-500',
};

const PLATFORM_LABELS: Record<string, string> = {
  copy: 'Copy Link',
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  twitter: 'X (Twitter)',
  email: 'Email',
};

export default function ShareAnalytics({ projectId, shareToken }: ShareAnalyticsProps) {
  const [data, setData] = useState<ShareAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    const result = await fetchShareAnalytics(projectId);
    setData(result);
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const completionRate = useMemo(() => {
    if (!data || data.total_plays === 0) return 0;
    return Math.round((data.total_completions / data.total_plays) * 100);
  }, [data]);

  const maxDayViews = useMemo(() => {
    if (!data?.views_by_day?.length) return 1;
    return Math.max(...data.views_by_day.map((d) => d.views), 1);
  }, [data]);

  const totalDevices = useMemo(() => {
    if (!data?.devices?.length) return 0;
    return data.devices.reduce((sum, d) => sum + d.count, 0);
  }, [data]);

  if (!shareToken) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-medium text-slate-400">Share Analytics</h2>
        </div>
        <p className="text-sm text-slate-500">
          Share your video to start tracking analytics. Click the Share button above to generate a link.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-medium text-slate-400">Share Analytics</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-medium text-slate-400">Share Analytics</h2>
        </div>
        <p className="text-sm text-slate-500">Unable to load analytics data.</p>
      </div>
    );
  }

  const hasData = data.total_views > 0;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <h2 className="text-sm font-semibold text-white">Share Analytics</h2>
        </div>
        <div className="flex items-center gap-3">
          {data.last_view && (
            <span className="text-[11px] text-slate-500">
              Last view: {new Date(data.last_view).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </span>
          )}
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="p-1.5 text-slate-400 hover:text-white rounded-md hover:bg-slate-800 transition-colors disabled:opacity-50"
            title="Refresh analytics"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {!hasData ? (
        <div className="px-6 py-10 text-center">
          <Eye className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-sm text-slate-400 mb-1">No views yet</p>
          <p className="text-xs text-slate-600">
            Share your link to start seeing analytics data here
          </p>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {/* Key metrics row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <MetricCard
              icon={Eye}
              label="Total Views"
              value={formatNumber(data.total_views)}
              color="text-blue-400"
            />
            <MetricCard
              icon={Users}
              label="Unique Viewers"
              value={formatNumber(data.unique_viewers)}
              color="text-purple-400"
            />
            <MetricCard
              icon={Play}
              label="Plays"
              value={formatNumber(data.total_plays)}
              color="text-green-400"
            />
            <MetricCard
              icon={CheckCircle2}
              label="Completions"
              value={`${completionRate}%`}
              subtitle={`${data.total_completions} total`}
              color="text-emerald-400"
            />
            <MetricCard
              icon={Clock}
              label="Avg. Watch"
              value={formatDuration(data.avg_watch_duration_ms)}
              subtitle={`${data.avg_watch_percentage}% avg`}
              color="text-amber-400"
            />
            <MetricCard
              icon={Share2}
              label="Shares"
              value={formatNumber(data.total_shares)}
              color="text-pink-400"
            />
          </div>

          {/* Views over time chart */}
          {data.views_by_day.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-slate-400 mb-3 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" />
                Views Over Time (30 days)
              </h3>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-end gap-[3px] h-24">
                  {data.views_by_day.map((day, i) => {
                    const height = Math.max(4, (day.views / maxDayViews) * 100);
                    const playHeight = day.plays > 0 ? Math.max(2, (day.plays / maxDayViews) * 100) : 0;
                    return (
                      <div
                        key={i}
                        className="flex-1 flex flex-col items-stretch justify-end gap-px group relative"
                      >
                        <div
                          className="bg-blue-500/70 rounded-t-sm min-w-[4px] transition-colors group-hover:bg-blue-400"
                          style={{ height: `${height}%` }}
                        />
                        {playHeight > 0 && (
                          <div
                            className="bg-green-500/50 rounded-b-sm min-w-[4px]"
                            style={{ height: `${playHeight}%` }}
                          />
                        )}
                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
                          <div className="bg-slate-700 text-white text-[10px] rounded-md px-2 py-1.5 whitespace-nowrap shadow-lg border border-slate-600">
                            <div className="font-medium mb-0.5">{formatDate(day.day)}</div>
                            <div className="text-blue-300">{day.views} views ({day.unique_views} unique)</div>
                            <div className="text-green-300">{day.plays} plays</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[10px] text-slate-600">
                    {data.views_by_day.length > 0 && formatDate(data.views_by_day[0].day)}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                      <span className="w-2 h-2 rounded-sm bg-blue-500/70 inline-block" />
                      Views
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                      <span className="w-2 h-2 rounded-sm bg-green-500/50 inline-block" />
                      Plays
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-600">
                    {data.views_by_day.length > 0 && formatDate(data.views_by_day[data.views_by_day.length - 1].day)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Bottom grid: Referrers, Devices, Shares, Regions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Top referrers */}
            {data.top_referrers.length > 0 && (
              <BreakdownCard title="Top Referrers" icon={Globe}>
                {data.top_referrers.map((ref, i) => (
                  <BreakdownRow
                    key={i}
                    label={ref.source}
                    count={ref.count}
                    total={data.total_views}
                    color="bg-blue-500"
                  />
                ))}
              </BreakdownCard>
            )}

            {/* Device breakdown */}
            {data.devices.length > 0 && (
              <BreakdownCard title="Devices" icon={Monitor}>
                {data.devices.map((dev, i) => {
                  const DevIcon = DEVICE_ICONS[dev.device] || Monitor;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <DevIcon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                      <BreakdownRow
                        label={dev.device.charAt(0).toUpperCase() + dev.device.slice(1)}
                        count={dev.count}
                        total={totalDevices}
                        color="bg-purple-500"
                      />
                    </div>
                  );
                })}
              </BreakdownCard>
            )}

            {/* Share platforms */}
            {data.share_platforms.length > 0 && (
              <BreakdownCard title="Share Channels" icon={Share2}>
                {data.share_platforms.map((sp, i) => (
                  <BreakdownRow
                    key={i}
                    label={PLATFORM_LABELS[sp.platform] || sp.platform}
                    count={sp.count}
                    total={data.total_shares}
                    color={PLATFORM_COLORS[sp.platform] || 'bg-slate-500'}
                  />
                ))}
              </BreakdownCard>
            )}

            {/* Browser breakdown */}
            {data.browsers.length > 0 && (
              <BreakdownCard title="Browsers" icon={Globe}>
                {data.browsers.map((b, i) => (
                  <BreakdownRow
                    key={i}
                    label={b.browser}
                    count={b.count}
                    total={data.total_views}
                    color="bg-amber-500"
                  />
                ))}
              </BreakdownCard>
            )}

            {/* Timezone / regions */}
            {data.timezones.length > 0 && (
              <BreakdownCard title="Viewer Regions" icon={Globe}>
                {data.timezones.map((tz, i) => (
                  <BreakdownRow
                    key={i}
                    label={formatTimezone(tz.timezone)}
                    count={tz.count}
                    total={data.total_views}
                    color="bg-teal-500"
                  />
                ))}
              </BreakdownCard>
            )}
          </div>

          {/* Time range */}
          {data.first_view && (
            <div className="text-[11px] text-slate-600 text-center pt-2 border-t border-slate-800">
              Tracking since {new Date(data.first_view).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  subtitle,
  color,
}: {
  icon: typeof Eye;
  label: string;
  value: string;
  subtitle?: string;
  color: string;
}) {
  return (
    <div className="bg-slate-800/50 rounded-lg px-3 py-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span className="text-[11px] text-slate-500 font-medium">{label}</span>
      </div>
      <div className="text-lg font-bold text-white leading-none">{value}</div>
      {subtitle && (
        <div className="text-[10px] text-slate-500 mt-1">{subtitle}</div>
      )}
    </div>
  );
}

function BreakdownCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof Globe;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-slate-800/30 rounded-lg p-4">
      <h4 className="text-xs font-medium text-slate-400 mb-3 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function BreakdownRow({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex-1">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-300 truncate mr-2">{label}</span>
        <span className="text-slate-500 flex-shrink-0 tabular-nums">
          {count} <span className="text-slate-600">({pct}%)</span>
        </span>
      </div>
      <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
